/**
 * The auth seam the rest of the web app consumes. Implementations: local dev
 * (bearer = seeded firebaseUid) and Firebase. The snapshot's `loading` state is
 * load-bearing: protected query observers must never mount while it holds.
 */

/**
 * Auth state as a discriminated union — `providerUid` keys the client-cache session.
 *
 * **`unavailable` is not `anonymous`**. It means the provider could not
 * *establish* an answer — the Firebase SDK chunk failed to load, the web config
 * threw, `authStateReady()` rejected — not that the caller is signed out.
 * Publishing `anonymous` there would tell a signed-in user they are signed out
 * and send them to a sign-in flow that needs the same broken SDK, which is a
 * confident wrong answer in place of an honest one.
 *
 * It is also **not** a fourth flavour of `loading`: `loading` resolves on its
 * own and is rendered as a spinner, and this status exists so a provider that
 * cannot answer never shows a spinner that never stops.
 *
 * Consumers must not extend their if-chains one status at a time. The rule is
 * to make the fall-through explicit — `if (snapshot.status !== "authenticated")`
 * — so the compiler narrows what follows to `authenticated` and any status
 * added later lands in a safe branch instead of the signed-in UI. Nothing here
 * is an exhaustive `switch`, so the type system will not otherwise say a word.
 */
export type AuthSnapshot =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "unavailable" }
  | { status: "authenticated"; providerUid: string };

/** Stable snapshot instances so `useSyncExternalStore` sees referential equality. */
export const LOADING_SNAPSHOT: AuthSnapshot = { status: "loading" };
/** The signed-out snapshot, shared for the same reason as {@link LOADING_SNAPSHOT}. */
export const ANONYMOUS_SNAPSHOT: AuthSnapshot = { status: "anonymous" };
/** See {@link AuthSnapshot} — the provider could not determine the caller's state. */
export const UNAVAILABLE_SNAPSHOT: AuthSnapshot = { status: "unavailable" };

/** Provider of bearer tokens + auth-state notifications. */
export interface TokenProvider {
  /** Current snapshot (stable reference between changes). */
  getSnapshot(): AuthSnapshot;
  /** Subscribe to snapshot changes; returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
  /** Bearer token for the next request, or null when unauthenticated. */
  getIdToken(): Promise<string | null>;
  /** Clear the identity (token). Analytics reset is the root lifecycle's job. */
  signOut(): Promise<void>;
}
